import type {
    ComponentTypePayload,
    ComponentTypeResponse,
} from '@/types/apps/component-type.type';
import type {
    ApiResponse,
    PaginatedResult,
    QueryParams,
} from '@/types/common/api.type';
import type { BaseApiClientInterface } from '@/types/service/base-api-client.interface';
import type { Creatable } from '@/types/service/creatable.interface';
import type { Deletable } from '@/types/service/deletable.interface';
import type { Exportable } from '@/types/service/exportable.interface';
import type { Importable } from '@/types/service/importable.interface';
import type { Indexable } from '@/types/service/indexable.interface';
import type { Showable } from '@/types/service/showable.interface';
import type { Updatable } from '@/types/service/updatable.interface';

type ComponentTypeListResponse = ApiResponse<
    PaginatedResult<ComponentTypeResponse>
>;

export class ComponentTypeService
    implements
        Showable,
        Updatable,
        Deletable,
        Creatable,
        Indexable,
        Importable,
        Exportable
{
    public constructor(private apiClient: BaseApiClientInterface) {}

    public endpoints = {
        base: () => '/api/PartTypes',
        detail: (id: string) => `/api/PartTypes/${id}`,
        import: () => '/api/device-type/import',
        export: () => '/api/device-type/export',
    };

    public index(params?: QueryParams): Promise<ComponentTypeListResponse> {
        return this.apiClient.fetchApi(this.endpoints.base(), params);
    }

    public detail(
        id: string,
        params?: QueryParams
    ): Promise<ApiResponse<ComponentTypeResponse>> {
        const url = this.endpoints.detail(id);

        return this.apiClient.fetchApi(url, params);
    }

    public create(
        payload: ComponentTypePayload
    ): Promise<ApiResponse<ComponentTypeResponse>> {
        return this.apiClient.postApi(this.endpoints.base(), payload);
    }

    public update(
        id: string,
        payload: ComponentTypePayload
    ): Promise<ApiResponse<ComponentTypeResponse>> {
        return this.apiClient.putApi(this.endpoints.detail(id), payload);
    }
    public destroy(
        ids: string[],
        isHardDeleted: boolean = false
    ): Promise<ApiResponse<void>> {
        const url = this.endpoints.base();

        return this.apiClient.destroyApi<void>(url, ids, isHardDeleted);
    }

    public async importFile(file: File): Promise<any> {
        const form = this.apiClient.buildFormData({ file });

        return this.apiClient.postApi<any>(this.endpoints.import(), form);
    }

    public async exportFile(): Promise<Blob> {
        return this.apiClient.fetchBlob(this.endpoints.export());
    }
}
